import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import styles from './index.module.less';
import { ProdCompareProps, } from 'base/interfaces/product-compare';
import useCompare from '@base/product/ProdCompare/useCompare';
import ProdAttrCell from '@base/product/ProdCompare/ProdAttrCell';
import useProductTagScale from 'base/hooks/useProductTagScale';
import { addEvent, removeEvent } from 'ava-util/dist/dom-event';
import { autoTrackImp } from '@base/lib/Tracker';
import { displayCtr } from 'base/utils/tracker';
import TabPartTitle from '@app/product/TabPartTitle';
import { useTrans } from 'base/utils/i18n';

const MODULE_ID = 'WEB compare items';

const ProdCompare: React.FC<ProdCompareProps> = ({ variantId, variantTrackCode }) => {
    const compare = useCompare(variantId);
    const tableWrapRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const { t } = useTrans();

    const setColumsWidth = () => {
        const wrap = tableWrapRef.current;
        if (!wrap) {
            return;
        }
        const wrapW = wrap.offsetWidth;
        const tr = wrap.querySelector('tr') as HTMLElement;
        const thW = (tr.querySelector('th') as HTMLElement).offsetWidth;
        const tds = tr.querySelectorAll('td');
        const tdW = Math.max(180, Math.floor((wrapW - thW) / tds.length))
        Array.prototype.map.call(tds, td => td.style.width = `${tdW}px`);
    };

    useProductTagScale({
        listRef: listRef?.current,
        monitorData: compare,
        resizable: true,
        multiple: 1.3,
    });

    useEffect(() => {
        if(compare) {
            setColumsWidth();
            displayCtr(MODULE_ID);

            const wrap = tableWrapRef.current;
            wrap && addEvent(wrap, 'scroll', autoTrackImp)
            return () => {
                wrap && removeEvent(wrap, 'scroll', autoTrackImp)
            }
        }
    }, [compare])

    if (!compare) {
        return null;
    }
    const {
        rows,
        products,
    } = compare;
    return compare ? (
        <div ref={listRef} data-t-mod={MODULE_ID} className={'wrapper'}>

            <TabPartTitle title={t('web_product_compare_title')}/>

            <div className={styles['table-wrap']} ref={tableWrapRef}>
                <table className={styles.table}>
                    <tbody>
                    {rows.map((row, rowIdx) => (
                        <tr
                            key={row.id}
                            className={cn({
                                'tr-info': rowIdx <= 2,
                                'tr-gray': rowIdx > 2 && rowIdx % 2 === 1,
                                'tr-white': rowIdx > 2 && rowIdx % 2 === 0,
                            })}
                        >
                            <th>
                                <div className="attr-name">{row.name}</div>
                            </th>
                            {products.map((product, productIdx) => (
                                <td
                                    key={product.variantId}
                                    className={cn(
                                        `td-${row.type}`,
                                        { 'td-out': !product.isStock },
                                        { 'td-cur-prod': product.variantId === variantId },
                                    )}
                                >
                                    <ProdAttrCell
                                        variantId={variantId}
                                        variantTrackCode={variantTrackCode}
                                        moduleId={MODULE_ID}
                                        trackIndex={productIdx}
                                        product={product}
                                        attributeId={row.id}
                                        type={row.type}
                                        imageSize={152}
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    ) : null;
};

export default ProdCompare;
